import { useEffect, useState, useCallback } from "react";
import {
  Drawer, Button, Tag, Icon, Spinner, Callout, InputGroup, TextArea,
  FormGroup, HTMLSelect, Card, Elevation, Tooltip,
} from "@blueprintjs/core";
import { api, type Lead, type FollowUp, type Note } from "../lib/api";

const PIPELINE_STAGES = ["unclaimed", "claimed", "contacted", "qualified", "won"] as const;
const TERMINAL = new Set(["won", "lost", "dead"]);

const STAGE_LABELS: Record<string, string> = {
  unclaimed: "New", claimed: "Claimed", contacted: "Contacted",
  interested: "Interested", qualified: "Qualified", won: "Won",
  lost: "Lost", dead: "Dead",
};

const FOLLOW_UP_TYPES = ["call", "email", "meeting", "task", "demo", "other"];

const TYPE_ICONS: Record<string, string> = {
  call: "phone", email: "envelope", meeting: "people",
  task: "tick-circle", demo: "desktop", other: "clipboard",
};

function errMsg(e: unknown): string {
  if (typeof e === "string") return e;
  if (e && typeof e === "object" && "message" in e) return String((e as any).message);
  return "Something went wrong";
}

function fmtDate(iso: string): string {
  try {
    return new Date(iso).toLocaleDateString("en-US", { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" });
  } catch { return iso; }
}

function fmtDateShort(iso: string): string {
  try {
    return new Date(iso).toLocaleDateString("en-US", { month: "short", day: "numeric" });
  } catch { return iso; }
}

function quickDate(offset: number): string {
  const d = new Date();
  d.setDate(d.getDate() + offset);
  return d.toISOString().split("T")[0];
}

function relativeDate(dateStr: string): string {
  const today = new Date().toISOString().split("T")[0];
  const tomorrow = quickDate(1);
  if (dateStr === today) return "Today";
  if (dateStr === tomorrow) return "Tomorrow";
  return fmtDateShort(dateStr);
}

function copyToClipboard(text: string) {
  navigator.clipboard.writeText(text).catch(() => {});
}

interface Props {
  leadId: string | null;
  isOpen: boolean;
  onClose: () => void;
  onLeadChanged?: () => void;
}

export function LeadDetailDrawer({ leadId, isOpen, onClose, onLeadChanged }: Props) {
  const [lead, setLead] = useState<Lead | null>(null);
  const [followUps, setFollowUps] = useState<FollowUp[]>([]);
  const [notes, setNotes] = useState<Note[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [actionLoading, setActionLoading] = useState(false);
  const [copiedField, setCopiedField] = useState("");

  const [editing, setEditing] = useState(false);
  const [editForm, setEditForm] = useState({ first_name: "", last_name: "", email: "", phone: "", job_title: "", linkedin_url: "" });

  const [showFuForm, setShowFuForm] = useState(false);
  const [fuForm, setFuForm] = useState({ title: "", type: "call", scheduled_date: "", description: "" });

  const [noteText, setNoteText] = useState("");
  const [showDeadDialog, setShowDeadDialog] = useState(false);
  const [deadReason, setDeadReason] = useState("");
  const [pendingStatus, setPendingStatus] = useState("");
  const [activeSection, setActiveSection] = useState<"activity" | "details">("activity");

  const fetchLead = useCallback(async () => {
    if (!leadId) return;
    setLoading(true); setError("");
    try {
      const [l, fu, n] = await Promise.all([
        api.get<Lead>(`/leads/${leadId}`),
        api.get<FollowUp[]>(`/leads/${leadId}/follow-ups`),
        api.get<Note[]>(`/leads/${leadId}/notes`),
      ]);
      setLead(l);
      setFollowUps(fu);
      setNotes(n);
      setEditForm({
        first_name: l.first_name, last_name: l.last_name,
        email: l.email || "", phone: l.phone || "",
        job_title: l.job_title || "", linkedin_url: l.linkedin_url || "",
      });
    } catch (e: unknown) { setError(errMsg(e)); }
    finally { setLoading(false); }
  }, [leadId]);

  useEffect(() => {
    if (isOpen && leadId) { fetchLead(); setEditing(false); setActiveSection("activity"); }
  }, [isOpen, leadId, fetchLead]);

  const refresh = () => { fetchLead(); onLeadChanged?.(); };

  const act = async (fn: () => Promise<void>) => {
    setActionLoading(true); setError("");
    try { await fn(); refresh(); }
    catch (e: unknown) {
      if ((e as any)?.status === 409) {
        setError(errMsg(e));
        setTimeout(refresh, 500);
      } else {
        setError(errMsg(e));
      }
    }
    finally { setActionLoading(false); }
  };

  const handleCopy = (text: string, field: string) => {
    copyToClipboard(text);
    setCopiedField(field);
    setTimeout(() => setCopiedField(""), 1500);
  };

  const handleStatusChange = (newStatus: string) => {
    if (newStatus === "dead") { setPendingStatus("dead"); setShowDeadDialog(true); return; }
    act(() => api.patch(`/leads/${lead!.id}/status`, { status: newStatus }));
  };

  const handleCreateFollowUp = () => {
    if (!fuForm.title || !fuForm.scheduled_date) return;
    act(async () => {
      await api.post(`/leads/${lead!.id}/follow-ups`, {
        title: fuForm.title, type: fuForm.type,
        scheduled_date: fuForm.scheduled_date,
        description: fuForm.description || null,
      });
      setShowFuForm(false);
      setFuForm({ title: "", type: "call", scheduled_date: "", description: "" });
    });
  };

  const stageIndex = (s: string) => PIPELINE_STAGES.indexOf(s as any);
  const currentIdx = lead ? stageIndex(lead.status) : -1;
  const isDead = lead?.status === "dead" || lead?.status === "lost";
  const isTerminal = lead ? TERMINAL.has(lead.status) : false;

  const overdueFollowUps = followUps.filter(f => f.status === "pending" && f.scheduled_date < new Date().toISOString().split("T")[0]);
  const pendingFollowUps = followUps.filter(f => f.status === "pending" && f.scheduled_date >= new Date().toISOString().split("T")[0]);
  const completedFollowUps = followUps.filter(f => f.status === "completed");
  const nextFu = [...overdueFollowUps, ...pendingFollowUps][0];

  type TimelineItem = { ts: string; type: "note" | "followup"; data: Note | FollowUp };
  const timeline: TimelineItem[] = [
    ...notes.map(n => ({ ts: n.created_at, type: "note" as const, data: n })),
    ...followUps.map(f => ({ ts: f.created_at, type: "followup" as const, data: f })),
  ].sort((a, b) => new Date(b.ts).getTime() - new Date(a.ts).getTime());

  return (
    <Drawer isOpen={isOpen} onClose={onClose} size="500px" position="right" title={null} hasBackdrop canOutsideClickClose className="lead-detail-drawer">
      {loading ? (
        <div style={{ display: "flex", justifyContent: "center", padding: 60 }}><Spinner size={32} /></div>
      ) : error && !lead ? (
        <Callout intent="danger" style={{ margin: 20 }}>{error}</Callout>
      ) : lead ? (
        <div style={{ display: "flex", flexDirection: "column", height: "100%" }}>
          {error && <Callout intent="danger" icon={null} style={{ margin: "12px 20px 0", fontSize: 13 }}>{error}</Callout>}

          {/* ── Header ── */}
          <div style={{ padding: "20px 24px 0", borderBottom: "1px solid #dce0e5" }}>
            <div style={{ display: "flex", alignItems: "flex-start", gap: 14, marginBottom: 14 }}>
              <div style={{
                width: 48, height: 48, borderRadius: 10,
                background: isTerminal ? (isDead ? "#f5c6cb" : "#c6f5ce") : "#d5e4f0",
                display: "flex", alignItems: "center", justifyContent: "center",
                flexShrink: 0, fontSize: 17, fontWeight: 700,
                color: isTerminal ? (isDead ? "#9e2b25" : "#1d7324") : "#2b6cb0",
              }}>
                {lead.first_name[0]}{lead.last_name[0]}
              </div>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                  <span style={{ fontSize: 18, fontWeight: 700, lineHeight: 1.2 }}>{lead.first_name} {lead.last_name}</span>
                  {lead.score > 0 && (
                    <Tag round minimal intent={lead.score >= 80 ? "success" : lead.score >= 50 ? "warning" : "none"} style={{ fontSize: 12, fontWeight: 700 }}>
                      {lead.score}
                    </Tag>
                  )}
                </div>
                <div style={{ fontSize: 13, color: "#5c7080", marginTop: 1 }}>{lead.job_title || "No title"}</div>
                <div style={{ fontSize: 13, color: "#5c7080", display: "flex", alignItems: "center", gap: 6, marginTop: 2 }}>
                  <Icon icon="office" size={12} color="#8a9ba8" />
                  <span>{lead.company_name || "Unknown"}</span>
                  {lead.company_domain && (
                    <a href={`https://${lead.company_domain}`} target="_blank" rel="noreferrer"
                      style={{ fontSize: 12, color: "#2b95d6", textDecoration: "none" }}>
                      {lead.company_domain}
                    </a>
                  )}
                </div>
              </div>
            </div>

            {/* Quick actions row */}
            <div style={{ display: "flex", gap: 6, marginBottom: 14, flexWrap: "wrap" }}>
              {!lead.claimed_by ? (
                <Button intent="primary" icon="hand" onClick={() => act(() => api.post(`/leads/${lead.id}/claim`))} loading={actionLoading} small>
                  Claim Lead
                </Button>
              ) : (
                <>
                  <Button icon="hand-up" onClick={() => act(() => api.post(`/leads/${lead.id}/release`))} loading={actionLoading} small outlined>
                    Release
                  </Button>
                  {!isTerminal && (
                    <Button icon="cross-circle" intent="danger" onClick={() => handleStatusChange("dead")} loading={actionLoading} small outlined>
                      Dead
                    </Button>
                  )}
                </>
              )}
              {lead.email ? (
                <Tooltip content={copiedField === "email" ? "Copied!" : "Copy email"} minimal>
                  <Button icon="envelope" small minimal onClick={() => handleCopy(lead.email!, "email")} />
                </Tooltip>
              ) : (
                <Tooltip content="Reveal email (~1 credit)" minimal>
                  <Button icon="envelope" small minimal intent="primary"
                    onClick={() => act(async () => { await api.post(`/leads/${lead.id}/reveal-email`); })}
                    loading={actionLoading} />
                </Tooltip>
              )}
              {lead.phone ? (
                <Tooltip content={copiedField === "phone" ? "Copied!" : "Copy phone"} minimal>
                  <Button icon="phone" small minimal onClick={() => handleCopy(lead.phone!, "phone")} />
                </Tooltip>
              ) : (
                <Tooltip content="Reveal phone (~5-10 credits)" minimal>
                  <Button icon="phone" small minimal intent="primary"
                    onClick={() => act(async () => { await api.post(`/leads/${lead.id}/reveal-phone`); })}
                    loading={actionLoading} />
                </Tooltip>
              )}
              {lead.linkedin_url && (
                <Tooltip content="Open LinkedIn" minimal>
                  <Button icon="link" small minimal onClick={() => window.open(lead.linkedin_url!, "_blank")} />
                </Tooltip>
              )}
              <div style={{ marginLeft: "auto" }}>
                <Button icon="edit" small minimal onClick={() => { setEditing(!editing); setActiveSection("details"); }} active={editing} />
              </div>
            </div>

            {/* Pipeline */}
            {isDead ? (
              <div style={{ marginBottom: 14, padding: "8px 12px", background: "#fff5f5", borderRadius: 6, border: "1px solid #f5c6cb", fontSize: 13, display: "flex", alignItems: "center", gap: 8 }}>
                <Icon icon="ban-circle" size={14} color="#c23030" />
                <span>Marked as <strong>{lead.status}</strong>{lead.dead_reason ? ` — ${lead.dead_reason}` : ""}</span>
              </div>
            ) : (
              <div style={{ display: "flex", marginBottom: 14 }}>
                {PIPELINE_STAGES.map((stage, i) => {
                  const isCurrent = lead.status === stage;
                  const isPast = currentIdx > i;
                  const isNext = lead.claimed_by && !isTerminal && i === currentIdx + 1;
                  const filled = isCurrent || isPast;

                  return (
                    <Tooltip key={stage} content={isNext ? `Advance to ${STAGE_LABELS[stage]}` : STAGE_LABELS[stage]} minimal>
                      <div
                        onClick={() => isNext && handleStatusChange(stage)}
                        style={{
                          flex: 1, textAlign: "center", padding: "7px 2px",
                          cursor: isNext ? "pointer" : "default",
                          background: isCurrent ? "#137cbd" : isPast ? "#48aff0" : isNext ? "#e8f4fd" : "#e8ecf0",
                          color: filled ? "#fff" : isNext ? "#137cbd" : "#8a9ba8",
                          fontSize: 10.5, fontWeight: 700, letterSpacing: 0.4, textTransform: "uppercase",
                          borderRight: i < PIPELINE_STAGES.length - 1 ? "2px solid #fff" : "none",
                          borderRadius: i === 0 ? "6px 0 0 6px" : i === PIPELINE_STAGES.length - 1 ? "0 6px 6px 0" : 0,
                          transition: "all .15s",
                          ...(isNext ? { boxShadow: "inset 0 0 0 1.5px #137cbd" } : {}),
                        }}
                      >
                        {STAGE_LABELS[stage]}
                      </div>
                    </Tooltip>
                  );
                })}
              </div>
            )}

            {/* Next action banner */}
            {nextFu && (
              <div style={{
                display: "flex", alignItems: "center", gap: 8, padding: "8px 12px", marginBottom: 14,
                background: overdueFollowUps.length > 0 ? "#fff5f5" : "#f0f7ff",
                borderRadius: 6, border: `1px solid ${overdueFollowUps.length > 0 ? "#f5c6cb" : "#b3d7ff"}`,
              }}>
                <Icon icon={overdueFollowUps.length > 0 ? "warning-sign" : "time"} size={14}
                  color={overdueFollowUps.length > 0 ? "#c23030" : "#137cbd"} />
                <div style={{ flex: 1, fontSize: 13 }}>
                  <strong>{nextFu.title}</strong>
                  <span style={{ color: "#5c7080" }}> · {relativeDate(nextFu.scheduled_date)}</span>
                </div>
                <Tooltip content="Mark done" minimal>
                  <Button icon="tick" small minimal intent="success"
                    onClick={() => act(() => api.patch(`/leads/${lead.id}/follow-ups/${nextFu.id}/complete`, { outcome: "completed" }))} />
                </Tooltip>
              </div>
            )}

            {/* Section tabs */}
            <div style={{ display: "flex", gap: 0, borderBottom: "none" }}>
              {(["activity", "details"] as const).map(tab => (
                <div
                  key={tab}
                  onClick={() => setActiveSection(tab)}
                  style={{
                    flex: 1, textAlign: "center", padding: "8px 0", cursor: "pointer",
                    fontSize: 12, fontWeight: 600, textTransform: "uppercase", letterSpacing: 0.5,
                    color: activeSection === tab ? "#137cbd" : "#8a9ba8",
                    borderBottom: activeSection === tab ? "2px solid #137cbd" : "2px solid transparent",
                    transition: "all .15s",
                  }}
                >
                  {tab === "activity" ? "Activity" : "Details"}
                </div>
              ))}
            </div>
          </div>

          {/* ── Scrollable content ── */}
          <div style={{ flex: 1, overflow: "auto", padding: "16px 24px 20px" }}>
            {activeSection === "activity" ? (
              <>
                {/* Quick add bar */}
                <div style={{ display: "flex", gap: 6, marginBottom: 16 }}>
                  <InputGroup
                    small fill placeholder="Add a note..." style={{ flex: 1 }}
                    value={noteText}
                    onChange={e => setNoteText(e.target.value)}
                    onKeyDown={e => { if (e.key === "Enter" && !e.shiftKey && noteText.trim()) { e.preventDefault(); act(async () => { await api.post(`/leads/${lead.id}/notes`, { content: noteText.trim(), is_pinned: false }); setNoteText(""); }); } }}
                  />
                  <Button small intent="primary" icon="send-message" disabled={!noteText.trim()} loading={actionLoading}
                    onClick={() => act(async () => { await api.post(`/leads/${lead.id}/notes`, { content: noteText.trim(), is_pinned: false }); setNoteText(""); })} />
                  {lead.claimed_by && !isTerminal && (
                    <Tooltip content="Schedule follow-up" minimal>
                      <Button small icon="calendar" onClick={() => { setShowFuForm(!showFuForm); }} />
                    </Tooltip>
                  )}
                </div>

                {/* Follow-up quick form */}
                {showFuForm && (
                  <Card elevation={Elevation.ONE} style={{ padding: 14, marginBottom: 14, borderLeft: "3px solid #137cbd" }}>
                    <div style={{ fontSize: 12, fontWeight: 600, color: "#137cbd", marginBottom: 8 }}>NEW FOLLOW-UP</div>
                    <InputGroup small placeholder="What needs to happen?" value={fuForm.title}
                      onChange={e => setFuForm(p => ({ ...p, title: e.target.value }))}
                      style={{ marginBottom: 8 }} />
                    <div style={{ display: "flex", gap: 6, alignItems: "center", marginBottom: 8, flexWrap: "wrap" }}>
                      <HTMLSelect value={fuForm.type} onChange={e => setFuForm(p => ({ ...p, type: e.target.value }))}>
                        {FOLLOW_UP_TYPES.map(t => <option key={t} value={t}>{t[0].toUpperCase() + t.slice(1)}</option>)}
                      </HTMLSelect>
                      {/* Quick date buttons */}
                      {[{ label: "Today", d: 0 }, { label: "Tomorrow", d: 1 }, { label: "Next Week", d: 7 }].map(({ label, d }) => (
                        <Button key={label} small minimal
                          active={fuForm.scheduled_date === quickDate(d)}
                          onClick={() => setFuForm(p => ({ ...p, scheduled_date: quickDate(d) }))}
                          style={{ fontSize: 11 }}>
                          {label}
                        </Button>
                      ))}
                      <InputGroup small type="date" value={fuForm.scheduled_date} style={{ width: 140 }}
                        onChange={e => setFuForm(p => ({ ...p, scheduled_date: e.target.value }))} />
                    </div>
                    <div style={{ display: "flex", gap: 6, justifyContent: "flex-end" }}>
                      <Button small onClick={() => setShowFuForm(false)}>Cancel</Button>
                      <Button small intent="primary" onClick={handleCreateFollowUp} loading={actionLoading}
                        disabled={!fuForm.title || !fuForm.scheduled_date}>Schedule</Button>
                    </div>
                  </Card>
                )}

                {/* Overdue follow-ups */}
                {overdueFollowUps.map(fu => <FollowUpItem key={fu.id} fu={fu} lead={lead}
                  onComplete={(id) => act(() => api.patch(`/leads/${lead.id}/follow-ups/${id}/complete`, { outcome: "completed" }))}
                  loading={actionLoading} isOverdue />)}

                {/* Pending follow-ups */}
                {pendingFollowUps.map(fu => <FollowUpItem key={fu.id} fu={fu} lead={lead}
                  onComplete={(id) => act(() => api.patch(`/leads/${lead.id}/follow-ups/${id}/complete`, { outcome: "completed" }))}
                  loading={actionLoading} />)}

                {/* Unified Timeline */}
                {timeline.length > 0 ? (
                  <div style={{ borderLeft: "2px solid #e1e8ed", marginLeft: 8, paddingLeft: 16, marginTop: 12 }}>
                    {timeline.map((item, i) => (
                      <div key={`${item.type}-${(item.data as any).id}`} style={{ position: "relative", paddingBottom: i < timeline.length - 1 ? 14 : 0 }}>
                        <div style={{
                          position: "absolute", left: -23, top: 3,
                          width: 12, height: 12, borderRadius: "50%",
                          background: item.type === "note" ? "#d5e4f0" : "#e8f7e9",
                          border: `2px solid ${item.type === "note" ? "#137cbd" : "#0d8050"}`,
                        }} />
                        {item.type === "note" ? (
                          <NoteItem n={item.data as Note} />
                        ) : (
                          <TimelineFollowUp fu={item.data as FollowUp} />
                        )}
                      </div>
                    ))}
                  </div>
                ) : (
                  <div style={{ textAlign: "center", padding: "24px 0", color: "#8a9ba8", fontSize: 13 }}>
                    <Icon icon="document" size={24} color="#d5dbe0" style={{ marginBottom: 8, display: "block" }} />
                    No activity yet. Add a note or schedule a follow-up to get started.
                  </div>
                )}
              </>
            ) : (
              /* Details Section */
              <>
                {editing ? (
                  <Card elevation={Elevation.ONE} style={{ padding: 16 }}>
                    <div style={{ fontSize: 12, fontWeight: 600, color: "#137cbd", marginBottom: 10 }}>EDIT CONTACT</div>
                    <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
                      <FormGroup label="First Name" style={{ margin: 0 }}>
                        <InputGroup small value={editForm.first_name} onChange={e => setEditForm(p => ({ ...p, first_name: e.target.value }))} />
                      </FormGroup>
                      <FormGroup label="Last Name" style={{ margin: 0 }}>
                        <InputGroup small value={editForm.last_name} onChange={e => setEditForm(p => ({ ...p, last_name: e.target.value }))} />
                      </FormGroup>
                      <FormGroup label="Email" style={{ margin: 0 }}>
                        <InputGroup small value={editForm.email} onChange={e => setEditForm(p => ({ ...p, email: e.target.value }))} />
                      </FormGroup>
                      <FormGroup label="Phone" style={{ margin: 0 }}>
                        <InputGroup small value={editForm.phone} onChange={e => setEditForm(p => ({ ...p, phone: e.target.value }))} />
                      </FormGroup>
                      <FormGroup label="Job Title" style={{ margin: 0 }}>
                        <InputGroup small value={editForm.job_title} onChange={e => setEditForm(p => ({ ...p, job_title: e.target.value }))} />
                      </FormGroup>
                      <FormGroup label="LinkedIn URL" style={{ margin: 0 }}>
                        <InputGroup small value={editForm.linkedin_url} onChange={e => setEditForm(p => ({ ...p, linkedin_url: e.target.value }))} />
                      </FormGroup>
                    </div>
                    <div style={{ display: "flex", gap: 8, marginTop: 12, justifyContent: "flex-end" }}>
                      <Button small onClick={() => setEditing(false)}>Cancel</Button>
                      <Button small intent="primary" onClick={() => act(async () => {
                        const payload: Record<string, string> = {};
                        for (const [k, v] of Object.entries(editForm)) {
                          if (v !== ((lead as any)[k] || "")) payload[k] = v || (undefined as any);
                        }
                        if (Object.keys(payload).length > 0) await api.patch(`/leads/${lead.id}`, payload);
                        setEditing(false);
                      })} loading={actionLoading}>Save Changes</Button>
                    </div>
                  </Card>
                ) : (
                  <>
                    <SectionTitle>Contact Information</SectionTitle>
                    <div style={{ marginBottom: 20 }}>
                      {lead.email ? (
                        <DetailRow icon="envelope" label="Email" value={lead.email}
                          href={lead.email ? `mailto:${lead.email}` : undefined}
                          onCopy={lead.email ? () => handleCopy(lead.email!, "email") : undefined}
                          copied={copiedField === "email"} />
                      ) : (
                        <RevealRow icon="envelope" label="Email" type="email" leadId={lead.id} onRevealed={refresh} />
                      )}
                      {lead.phone ? (
                        <DetailRow icon="phone" label="Phone" value={lead.phone}
                          onCopy={lead.phone ? () => handleCopy(lead.phone!, "phone") : undefined}
                          copied={copiedField === "phone"} />
                      ) : (
                        <RevealRow icon="phone" label="Phone" type="phone" leadId={lead.id} onRevealed={refresh} />
                      )}
                      <DetailRow icon="id-number" label="Title" value={lead.job_title} />
                      <DetailRow icon="link" label="LinkedIn" value={lead.linkedin_url ? "View Profile" : null}
                        href={lead.linkedin_url || undefined} />
                    </div>

                    <SectionTitle>Company</SectionTitle>
                    <div style={{ marginBottom: 20 }}>
                      <DetailRow icon="office" label="Name" value={lead.company_name} />
                      <DetailRow icon="globe" label="Domain" value={lead.company_domain}
                        href={lead.company_domain ? `https://${lead.company_domain}` : undefined} />
                    </div>

                    <SectionTitle>Lead Details</SectionTitle>
                    <div style={{ marginBottom: 20 }}>
                      <DetailRow icon="endorsed" label="Source" value={
                        ({ lusha: "B2B Database", lusha_search: "B2B Database", apollo: "B2B Database", apollo_search: "B2B Database", apollo_enrich: "B2B Database", lusha_enrich: "B2B Database", b2b_database: "B2B Database", google_places: "Google Places", osm: "OpenStreetMap" } as Record<string, string>)[lead.source_type ?? ""] || lead.source_type || "—"
                      } />
                      <DetailRow icon="star" label="Score" value={lead.score > 0 ? String(lead.score) : "Not scored"} />
                      <DetailRow icon="time" label="Created" value={fmtDate(lead.created_at)} />
                      {lead.last_activity_at && (
                        <DetailRow icon="refresh" label="Last Activity" value={fmtDate(lead.last_activity_at)} />
                      )}
                      {lead.claimed_by && (
                        <DetailRow icon="person" label="Claimed By" value={lead.claimer_name || "—"} />
                      )}
                    </div>

                    {/* Follow-ups summary in details */}
                    <SectionTitle>Follow-ups ({followUps.length})</SectionTitle>
                    {followUps.length === 0 ? (
                      <div style={{ fontSize: 13, color: "#8a9ba8", padding: "4px 0 16px" }}>None scheduled</div>
                    ) : (
                      <div style={{ marginBottom: 16 }}>
                        {overdueFollowUps.map(fu => <FollowUpItem key={fu.id} fu={fu} lead={lead}
                          onComplete={(id) => act(() => api.patch(`/leads/${lead.id}/follow-ups/${id}/complete`, { outcome: "completed" }))}
                          loading={actionLoading} isOverdue />)}
                        {pendingFollowUps.map(fu => <FollowUpItem key={fu.id} fu={fu} lead={lead}
                          onComplete={(id) => act(() => api.patch(`/leads/${lead.id}/follow-ups/${id}/complete`, { outcome: "completed" }))}
                          loading={actionLoading} />)}
                        {completedFollowUps.map(fu => <FollowUpItem key={fu.id} fu={fu} lead={lead}
                          onComplete={() => {}} loading={false} />)}
                      </div>
                    )}
                  </>
                )}
              </>
            )}
          </div>

          {/* Dead reason dialog */}
          {showDeadDialog && (
            <div style={{ position: "fixed", top: 0, left: 0, right: 0, bottom: 0, background: "rgba(0,0,0,0.35)", zIndex: 30, display: "flex", alignItems: "center", justifyContent: "center" }}
              onClick={() => setShowDeadDialog(false)}>
              <Card elevation={Elevation.FOUR} style={{ padding: 24, width: 400, borderRadius: 10 }} onClick={e => e.stopPropagation()}>
                <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 16 }}>
                  <Icon icon="ban-circle" size={20} color="#c23030" />
                  <h4 style={{ margin: 0 }}>Mark Lead as Dead</h4>
                </div>
                <FormGroup label="Reason (optional)" style={{ marginBottom: 12 }}>
                  <TextArea fill value={deadReason} onChange={e => setDeadReason(e.target.value)}
                    placeholder="e.g. No budget, wrong contact, competitor chosen..." rows={3} />
                </FormGroup>
                <div style={{ display: "flex", gap: 8, justifyContent: "flex-end" }}>
                  <Button onClick={() => setShowDeadDialog(false)}>Cancel</Button>
                  <Button intent="danger" onClick={() => act(async () => {
                    await api.patch(`/leads/${lead.id}/status`, { status: pendingStatus, reason: deadReason });
                    setShowDeadDialog(false); setDeadReason(""); setPendingStatus("");
                  })} loading={actionLoading}>Confirm</Button>
                </div>
              </Card>
            </div>
          )}
        </div>
      ) : null}
    </Drawer>
  );
}

function SectionTitle({ children }: { children: React.ReactNode }) {
  return (
    <div style={{ fontSize: 11, fontWeight: 700, textTransform: "uppercase", letterSpacing: 0.6, color: "#8a9ba8", marginBottom: 8, paddingBottom: 4, borderBottom: "1px solid #f0f2f4" }}>
      {children}
    </div>
  );
}

function RevealRow({ icon, label, type, leadId, onRevealed }: {
  icon: string; label: string; type: "email" | "phone"; leadId: string; onRevealed: () => void;
}) {
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState("");

  const handleReveal = async () => {
    setLoading(true);
    setErr("");
    try {
      await api.post(`/leads/${leadId}/reveal-${type}`);
      onRevealed();
    } catch (ex) {
      setErr(errMsg(ex));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={{ display: "flex", alignItems: "center", padding: "6px 0", borderBottom: "1px solid #f8f9fa" }}>
      <Icon icon={icon as any} size={13} color="#8a9ba8" style={{ marginRight: 10, flexShrink: 0 }} />
      <span style={{ fontSize: 12, color: "#8a9ba8", width: 80, flexShrink: 0 }}>{label}</span>
      <div style={{ flex: 1, display: "flex", alignItems: "center", gap: 6 }}>
        <Tooltip content={err || `~${type === "email" ? "1" : "5-10"} credits`} minimal intent={err ? "danger" : "none"}>
          <Button
            small
            minimal
            icon={type === "email" ? "envelope" : "phone"}
            intent="primary"
            loading={loading}
            onClick={handleReveal}
            style={{ fontSize: 11 }}
          >
            Reveal {label}
          </Button>
        </Tooltip>
      </div>
    </div>
  );
}

function DetailRow({ icon, label, value, href, onCopy, copied }: {
  icon: string; label: string; value: string | null | undefined;
  href?: string; onCopy?: () => void; copied?: boolean;
}) {
  return (
    <div style={{ display: "flex", alignItems: "center", padding: "6px 0", borderBottom: "1px solid #f8f9fa" }}>
      <Icon icon={icon as any} size={13} color="#8a9ba8" style={{ marginRight: 10, flexShrink: 0 }} />
      <span style={{ fontSize: 12, color: "#8a9ba8", width: 80, flexShrink: 0 }}>{label}</span>
      <div style={{ flex: 1, fontSize: 13, minWidth: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
        {value ? (
          href ? (
            <a href={href} target="_blank" rel="noreferrer" style={{ color: "#2b95d6", textDecoration: "none" }}>{value}</a>
          ) : (
            <span style={{ color: "#182026" }}>{value}</span>
          )
        ) : (
          <span style={{ color: "#ced4da" }}>--</span>
        )}
      </div>
      {onCopy && (
        <Tooltip content={copied ? "Copied!" : "Copy"} minimal>
          <Button icon={copied ? "tick" : "duplicate"} small minimal style={{ marginLeft: 4 }}
            intent={copied ? "success" : "none"} onClick={onCopy} />
        </Tooltip>
      )}
    </div>
  );
}

function NoteItem({ n }: { n: Note }) {
  return (
    <div style={{ paddingBottom: 4 }}>
      <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 2 }}>
        <span style={{ fontWeight: 600, fontSize: 12, color: "#394b59" }}>{n.author_name || "System"}</span>
        <span style={{ fontSize: 11, color: "#8a9ba8" }}>{fmtDate(n.created_at)}</span>
      </div>
      <div style={{ fontSize: 13, color: "#394b59", whiteSpace: "pre-wrap", lineHeight: 1.5 }}>{n.content}</div>
    </div>
  );
}

function TimelineFollowUp({ fu }: { fu: FollowUp }) {
  const isDone = fu.status === "completed";
  return (
    <div style={{ paddingBottom: 4 }}>
      <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 2 }}>
        <Icon icon={(TYPE_ICONS[fu.type] || "clipboard") as any} size={12} color={isDone ? "#8a9ba8" : "#0d8050"} />
        <span style={{ fontSize: 12, fontWeight: 600, color: isDone ? "#8a9ba8" : "#394b59", textDecoration: isDone ? "line-through" : "none" }}>
          {fu.title}
        </span>
        <span style={{ fontSize: 11, color: "#8a9ba8" }}>· {relativeDate(fu.scheduled_date)}</span>
        {isDone && <Tag minimal round intent="success" style={{ fontSize: 10 }}>Done</Tag>}
      </div>
    </div>
  );
}

function FollowUpItem({ fu, lead, onComplete, loading, isOverdue }: {
  fu: FollowUp; lead: Lead; onComplete: (id: string) => void; loading: boolean; isOverdue?: boolean;
}) {
  const isDone = fu.status === "completed";
  return (
    <div style={{
      display: "flex", alignItems: "center", gap: 10, padding: "8px 12px", marginBottom: 4,
      background: isOverdue ? "#fff5f5" : isDone ? "#f8f9fa" : "#fff",
      borderRadius: 6, border: `1px solid ${isOverdue ? "#f5c6cb" : "#e1e8ed"}`,
    }}>
      <Icon icon={(TYPE_ICONS[fu.type] || "clipboard") as any} size={14}
        color={isDone ? "#8a9ba8" : isOverdue ? "#c23030" : "#5c7080"} />
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontSize: 13, fontWeight: 500, color: isDone ? "#8a9ba8" : "#182026",
          textDecoration: isDone ? "line-through" : "none" }}>{fu.title}</div>
        <div style={{ fontSize: 11, color: isOverdue ? "#c23030" : "#8a9ba8" }}>
          {relativeDate(fu.scheduled_date)}{fu.creator_name ? ` · ${fu.creator_name}` : ""}
        </div>
      </div>
      {!isDone && (
        <Tooltip content="Mark complete" minimal>
          <Button icon="tick" small minimal intent="success" onClick={() => onComplete(fu.id)} loading={loading} />
        </Tooltip>
      )}
      {isDone && fu.outcome && <Tag minimal round style={{ fontSize: 10 }}>{fu.outcome}</Tag>}
    </div>
  );
}
